import React, { useState, useRef, useCallback } from 'react';
import UIButton from '@/components/shared/UIButton';
import FilesApi from '@/api/files';
import './UIAvatarUpload.scss';

export interface AvatarFile {
  file: File;
  preview: string;
  isValid: boolean;
  error?: string;
  uploadedUrl?: string;
  uploadedFileId?: number; // додати це поле
}

export interface UIAvatarUploadProps {
  /**
   * Поточний аватар (URL)
   */
  currentAvatar?: string;

  /**
   * Плейсхолдер при відсутності аватара
   */
  placeholder?: string;

  /**
   * Максимальний розмір файлу в байтах (за замовчуванням 5MB)
   */
  maxSize?: number;

  /**
   * Дозволені типи файлів
   */
  acceptedTypes?: string[];

  /**
   * Колбек при виборі файлу - тепер викликається ПІСЛЯ завантаження
   */
  onFileSelect?: (file: AvatarFile | null) => void;

  /**
   * Колбек при видаленні аватара
   */
  onRemove?: () => void;

  /**
   * Колбек при помилці
   */
  onError?: (error: string) => void;

  /**
   * Стан завантаження (зовнішній)
   */
  loading?: boolean;

  /**
   * Заблокований стан
   */
  disabled?: boolean;

  /**
   * Додатковий CSS клас
   */
  className?: string;
}

// Parse environment variable for max avatar size, with a fallback
const maxAvatarSizeMB = parseInt(process.env.REACT_APP_MAX_AVATAR_SIZE_MB || '5', 10);

// Константи для валідації
export const AVATAR_VALIDATION = {
  MAX_SIZE: maxAvatarSizeMB * 1024 * 1024, // Convert MB to bytes
  ACCEPTED_TYPES: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'],
  ACCEPTED_EXTENSIONS: ['.jpg', '.jpeg', '.png', '.webp'],
};


const mockData = {
  currentAvatar: undefined,
  placeholder: undefined,
  maxSize: AVATAR_VALIDATION.MAX_SIZE,
  acceptedTypes: AVATAR_VALIDATION.ACCEPTED_TYPES,
  loading: false,
  disabled: false,
};

const baseClass = 'UIAvatarUpload';

const UIAvatarUpload: React.FC<UIAvatarUploadProps> = ({
                                                         currentAvatar = mockData.currentAvatar,
                                                         placeholder = mockData.placeholder,
                                                         maxSize = mockData.maxSize,
                                                         acceptedTypes = mockData.acceptedTypes,
                                                         onFileSelect = () => {},
                                                         onRemove = () => {},
                                                         onError = () => {},
                                                         loading = mockData.loading,
                                                         disabled = mockData.disabled,
                                                         className = '',
                                                       }) => {
  const [selectedFile, setSelectedFile] = useState<AvatarFile | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false); // Внутрішній стан завантаження
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Валідація файлу
  const validateFile = useCallback(
    (file: File): Pick<AvatarFile, 'file' | 'preview' | 'isValid' | 'error'> => {
      const errors: string[] = [];

      // Перевірка типу
      if (!acceptedTypes.includes(file.type)) {
        errors.push(
          `Дозволені формати: ${acceptedTypes.map((t) => t.split('/')[1]).join(', ')}`,
        );
      }

      // Перевірка розміру
      if (file.size > maxSize) {
        errors.push(
          `Максимальний розмір: ${Math.round(maxSize / 1024 / 1024)}MB`,
        );
      }

      const preview = URL.createObjectURL(file);
      const isValid = errors.length === 0;

      return {
        file,
        preview,
        isValid,
        error: errors.length > 0 ? errors.join(', ') : undefined,
      };
    },
    [acceptedTypes, maxSize],
  );

  // Завантаження файлу на сервер
  const uploadFile = async (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = async () => {
        try {
          const base64Content = reader.result as string;

          const response = await FilesApi.uploadAvatar({
            content: base64Content,
            originalName: file.name,
          });

          resolve(response.data.url);
        } catch (error) {
          console.error('Error uploading avatar:', error);
          reject(error);
        }
      };

      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  };

  // Обробка вибору файлу
  const handleFileSelect = useCallback(
    async (file: File) => {
      const validatedFile = validateFile(file);

      if (!validatedFile.isValid) {
        onError(validatedFile.error || 'Невалідний файл');
        return;
      }

      // Створюємо тимчасовий об'єкт файла з preview
      const tempAvatarFile: AvatarFile = {
        ...validatedFile,
        uploadedUrl: undefined,
        uploadedFileId: undefined, // додати
      };

      setSelectedFile(tempAvatarFile);
      setIsUploading(true);


      try {
        // Завантажуємо файл на сервер
        const response = await FilesApi.uploadAvatar({
          content: await fileToBase64(file),
          originalName: file.name,
        });

        // Оновлюємо файл з URL та ID
        const uploadedAvatarFile: AvatarFile = {
          ...tempAvatarFile,
          uploadedUrl: response.data.url,
          uploadedFileId: response.data.id, // додати
        };

        setSelectedFile(uploadedAvatarFile);
        onFileSelect(uploadedAvatarFile);
      } catch (error) {
        // обробка помилки без змін
      } finally {
        setIsUploading(false);
      }
    },
    [validateFile, onFileSelect, onError],
  );

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  };


  // Клік на кнопку Upload
  const handleUploadClick = useCallback(() => {
    if (disabled || loading || isUploading) return;
    fileInputRef.current?.click();
  }, [disabled, loading, isUploading]);

  // Обробка input change
  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        handleFileSelect(file);
      }
      // Очищуємо input для можливості вибрати той же файл знову
      event.target.value = '';
    },
    [handleFileSelect],
  );

  // Кнопка Remove
  const handleRemoveClick = useCallback(() => {
    if (disabled || loading || isUploading) return;

    setSelectedFile(null);
    onRemove();

    // Очищуємо preview URL щоб уникнути memory leak
    if (selectedFile?.preview) {
      URL.revokeObjectURL(selectedFile.preview);
    }
  }, [disabled, loading, isUploading, onRemove, selectedFile]);

  // Drag & Drop обробники
  const handleDragEnter = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled && !loading && !isUploading) {
        setIsDragOver(true);
      }
    },
    [disabled, loading, isUploading],
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);

      if (disabled || loading || isUploading) return;

      const files = Array.from(e.dataTransfer.files);
      const imageFile = files.find((file) =>
        acceptedTypes.includes(file.type as string),
      );

      if (imageFile) {
        handleFileSelect(imageFile);
      } else {
        onError('Будь ласка, виберіть зображення у підтримуваному форматі');
      }
    },
    [disabled, loading, isUploading, acceptedTypes, handleFileSelect, onError],
  );

  // Отримуємо поточне зображення для показу
  const getCurrentImageSrc = useCallback(() => {
    if (selectedFile?.preview) {
      return selectedFile.preview;
    }

    if (currentAvatar) {
      return currentAvatar;
    }

    return placeholder || '/ava/photo.png';
  }, [selectedFile, currentAvatar, placeholder]);

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    e.currentTarget.src = '/ava/photo.png';
  };

  const hasAvatar = !!(selectedFile || currentAvatar);
  const isComponentLoading = loading || isUploading;

  return (
    <div className={`${baseClass} ${className}`}>
      {/* Прихований input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={acceptedTypes.join(',')}
        onChange={handleInputChange}
        disabled={disabled || isComponentLoading}
        style={{ display: 'none' }}
      />

      {/* Фото та кнопки */}
      <div className={`${baseClass}__photo-buttons`}>
        <div className={`${baseClass}__photo`}>
          <img
            src={getCurrentImageSrc()}
            alt="Avatar"
            onError={handleImageError}
          />
          {isComponentLoading && (
            <div className={`${baseClass}__photo-loading`}>
              <div className={`${baseClass}__spinner`} />
            </div>
          )}
        </div>

        <UIButton
          variant="secondary"
          size="small"
          onClick={handleUploadClick}
          disabled={disabled || isComponentLoading}
          loading={isUploading}
          className={`${baseClass}__button--upload`}
        >
          Upload
        </UIButton>

        <UIButton
          variant="secondary"
          size="small"
          onClick={handleRemoveClick}
          disabled={disabled || isComponentLoading || !hasAvatar}
          className={`${baseClass}__button--remove`}
        >
          Remove
        </UIButton>
      </div>

      {/* Drag & Drop зона */}
      <div
        className={`${baseClass}__drag-drop ${isDragOver ? `${baseClass}__drag-drop--drag-over` : ''}`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={handleUploadClick}
      >
        <div className={`${baseClass}__drag-drop-content`}>
          {/* Іконка зображення */}
          <div className={`${baseClass}__drag-drop-icon`}>
            <svg width="24" height="18" viewBox="0 0 24 18" fill="none">
              <circle cx="8.5" cy="4.5" r="1.5" fill="currentColor" />
              <path
                d="M6 0C4.89543 0 4 0.895431 4 2V12C4 13.1046 4.89543 14 6 14H18C19.1046 14 20 13.1046 20 12V2C20 0.895431 19.1046 0 18 0H6ZM18 1C18.5523 1 19 1.44772 19 2V8.5L15.2254 6.55279C15.0332 6.45661 14.8003 6.49434 14.6464 6.64645L10.9373 10.3566L8.27918 8.58397C8.08116 8.45185 7.8168 8.47794 7.64645 8.64645L5 11V2C5 1.44772 5.44772 1 6 1H18Z"
                fill="currentColor"
              />
            </svg>
          </div>

          {/* Текст */}
          <span className={`${baseClass}__drag-drop-text`}>
            {isUploading ? 'Uploading...' : 'Drag image here or Browse Files'}
          </span>
        </div>
      </div>

      {/* Повідомлення про помилку */}
      {selectedFile && !selectedFile.isValid && (
        <div className={`${baseClass}__error`}>{selectedFile.error}</div>
      )}
    </div>
  );
};

export default UIAvatarUpload;